import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import styles from './TermsDetails.module.scss';
import { fetchAndParseTerms } from './TermsDetails.utils.ts';

export type TermsDetailsProps = {
  /** The label for the trigger */
  label: string;
  /** The a11y loading text for the loading state */
  loadingText: string;
  /** The absolute URL to the page which contains the T&C's on nespresso.com */
  url: string;
  onToggled: () => void;
  status?: TermsState['status'];
};

type TermsState = {
  status: 'INITIAL' | 'PENDING' | 'LOADED' | 'ERROR';
  html: string;
};

/**
 * @deprecated - Replaced by "Accordion" component with size="small".
 *
 * Implementation notes:
 * - Move data fetching to config data
 * - Already replaced in AboutSubscription
 * - Pending replacement in DiscoverPromotionsDrawer
 *
 * Reference: Task ITNP-153135
 */
export default function TermsDetails({
  label,
  loadingText,
  url,
  status = 'INITIAL',
  onToggled
}: TermsDetailsProps) {
  const [terms, setTerms] = useState<TermsState>({
    status,
    html: ''
  });

  const handleToggle = (event: JSX.TargetedEvent<HTMLDetailsElement>) => {
    onToggled();
    const detailsElem = event.target as HTMLDetailsElement;
    const { open } = detailsElem;
    if (!open || terms.status !== 'INITIAL') return;
    fetchTerms();
  };

  async function fetchTerms() {
    setTerms({ status: 'PENDING', html: '' });

    const html = await fetchAndParseTerms(url);
    if (!html) {
      // @todo - handle error fetching terms
      return;
    }
    setTerms({ status: 'LOADED', html });
  }

  return (
    <details onToggle={(e) => handleToggle(e)} class={styles.details}>
      <summary class={styles.summary}>{label}</summary>
      {terms.status === 'LOADED' && (
        <div class={styles.wrapper}>
          <FormattedText className={styles.content}>{terms.html}</FormattedText>
        </div>
      )}
      {terms.status === 'PENDING' && (
        <div class={styles.loadingWrapper}>
          <TermsLoading loadingText={loadingText} />
        </div>
      )}
    </details>
  );
}

type TermsLoadingProps = {
  loadingText: string;
};

function TermsLoading({ loadingText }: TermsLoadingProps) {
  return (
    <div class={styles.loadingContainer}>
      <span class={styles.loadingText}>{loadingText}</span>
      <span class={styles.loading}>
        <svg
          width="21"
          height="21"
          stroke="#000"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g class={styles.spinner}>
            <circle cx="12" cy="12" r="9.5" fill="none" strokeWidth="3" />
          </g>
        </svg>
      </span>
    </div>
  );
}
